import { ApiError } from "./client";
import { isReal, MOCK_BUILD, type ApiArea } from "./areas";

// The adapter switch. Every area's API object is the real HTTP client for the methods whose area is live
// (src/api/areas.ts). The other methods answer from the in-memory mock registered for the module's area by
// src/mocks: installed at startup in mock builds, loaded on first use in real builds (demo data), or, when
// the area has one, from a device-local stand-in (`local`) that a real build prefers over the mock.

export const USE_MOCK = MOCK_BUILD;

/** The mock modules (src/mocks/<area>.ts). */
export type Area =
  | "auth"
  | "captures"
  | "cards"
  | "hoardex"
  | "market"
  | "progression"
  | "wallet"
  | "profile"
  | "notifications"
  | "settings"
  | "home"
  | "boosters"
  | "cosmetics"
  | "battles"
  | "admin"
  | "sharing"
  | "safety"
  | "growth";

const mocks = new Map<Area, object>();
let loadDemo: ((area: Area) => Promise<void>) | null = null;

/** Every mock call, `area.method`, in order: the mock answers in memory, so this is the only way to count
 * what a screen asks (the API call budget, CONTRIBUTING.md). Read by tests and `window.__apiCalls`. */
export const mockCalls: string[] = [];

/** Called by src/mocks when installed. */
export function registerMock(area: Area, implementation: object) {
  const counted = Object.fromEntries(
    Object.entries(implementation).map(([key, value]) => [
      key,
      typeof value === "function"
        ? (...args: unknown[]) => {
            mockCalls.push(`${area}.${key}`);
            return (value as (...a: unknown[]) => unknown).apply(implementation, args);
          }
        : value,
    ]),
  );
  mocks.set(area, counted);
  if (MOCK_BUILD && typeof window !== "undefined")
    (window as unknown as { __apiCalls?: string[] }).__apiCalls = mockCalls;
}

/** Real builds with demo areas: how to load a module's mock, the first time one of its demo methods is
 * called. Each module loads on its own (once), so a screen downloads only the mock it shows. */
export function setDemoLoader(load: (area: Area) => Promise<void>) {
  const loading = new Map<Area, Promise<void>>();
  loadDemo = (area) => {
    let pending = loading.get(area);
    if (!pending) loading.set(area, (pending = load(area)));
    return pending;
  };
}

type Methods<T> = Partial<Record<keyof T & string, ApiArea>>;

export interface AdapterOptions<T> {
  /** The area of each method that doesn't belong to the module's own area. */
  areas?: Methods<T>;
  /** Device-local stand-ins a real build uses while the area isn't live, instead of demo data. */
  local?: Partial<T>;
}

export function adapter<T extends object>(area: Area, http: T, options: AdapterOptions<T> = {}): T {
  const areaOf = (key: string): ApiArea => options.areas?.[key as keyof T & string] ?? (area as ApiArea); // a module named after no API area (sharing) maps every method
  const keys = Object.keys(http);
  if (keys.every((key) => isReal(areaOf(key)))) return http;
  return new Proxy(http, {
    get(target, key) {
      if (typeof key !== "string" || isReal(areaOf(key))) return Reflect.get(target, key);
      const local = options.local as Record<string, unknown> | undefined;
      if (!MOCK_BUILD && local && key in local) return local[key];
      const mock = mocks.get(area) as Record<string, unknown> | undefined;
      if (mock) return mock[key];
      if (!loadDemo) return Reflect.get(target, key);
      const load = loadDemo;
      return async (...args: unknown[]) => {
        await load(area);
        const method = (mocks.get(area) as Record<string, unknown> | undefined)?.[key];
        if (typeof method !== "function") throw new ApiError(501, "Not available", "not_available");
        return (method as (...a: unknown[]) => unknown)(...args);
      };
    },
  });
}
